import React, { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { AudioProgressSlider } from './progressSlider'
import {
    AudioPauseButton,
    AudioPlayBackButton,
    AudioPlayBackward10Button,
    AudioPlayButton,
    AudioPlayForward10Button,
    AudioPlayForwardButton,
} from './button'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
`

const Buttons = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    gap: 12px;
`
interface Props {
    url: string
    onTimeUpdate?: (time: number) => void
    onStart?: () => void
    onStop?: () => void
    onError?: (error: string) => void
}

export const AudioPlayer: FC<Props> = ({
    url,
    onTimeUpdate,
    onStart,
    onStop,
    onError,
}) => {
    const ref = useRef<HTMLAudioElement | null>(null)
    const [currentTime, setCurrentTime] = useState(0)
    const [bufferedTime, setBufferedTime] = useState(0)
    const [maxTime, setMaxTime] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)

    useEffect(() => {
        console.log('*** URL:', url)
        return () => {
            ref.current?.pause()
            setIsPlaying(false)
            onStop?.()
        }
    }, [url])

    return (
        <Container>
            <audio
                preload="metadata"
                src={url}
                ref={ref}
                onDurationChange={() => {
                    if (ref.current?.duration !== Infinity) {
                        setMaxTime(Math.floor(ref.current?.duration ?? 0))
                    }
                }}
                onProgress={() => {
                    if (ref.current) {
                        const bufferSize = ref.current.buffered.length
                        if (bufferSize > 0) {
                            setBufferedTime(
                                Math.floor(
                                    ref.current.buffered.end(bufferSize - 1)
                                )
                            )
                        }
                    }
                }}
                onTimeUpdate={() => {
                    const updatedTime = Math.floor(
                        ref.current?.currentTime ?? 0
                    )
                    setCurrentTime(updatedTime)
                    if (onTimeUpdate) {
                        onTimeUpdate(updatedTime)
                    }
                }}
                onEnded={() => {
                    setIsPlaying(false)
                    onStop?.()
                }}
            />
            <Buttons>
                <AudioPlayBackward10Button
                    onClick={() => {
                        if (ref.current) {
                            const newTime = Math.max(
                                0,
                                ref.current.currentTime - 10
                            )
                            ref.current.currentTime = newTime
                            setCurrentTime(Math.floor(newTime))
                        }
                    }}
                />
                <AudioPlayBackButton
                    onClick={() => {
                        if (ref.current) {
                            ref.current.currentTime = 0
                            setCurrentTime(0)
                        }
                    }}
                />
                {!isPlaying ? (
                    <AudioPlayButton
                        onClick={() => {
                            if (!url) {
                                onError?.('Missing url')
                                return
                            }

                            ref.current?.play()
                            setIsPlaying(true)
                            onStart?.()
                        }}
                    />
                ) : (
                    <AudioPauseButton
                        onClick={() => {
                            if (isPlaying) {
                                ref.current?.pause()
                                setIsPlaying(false)
                                onStop?.()
                            }
                        }}
                    />
                )}
                <AudioPlayForwardButton
                    onClick={() => {
                        if (ref.current) {
                            ref.current.currentTime = maxTime
                            setCurrentTime(maxTime)
                        }
                    }}
                />
                <AudioPlayForward10Button
                    onClick={() => {
                        if (ref.current) {
                            const newTime = Math.min(
                                maxTime,
                                ref.current.currentTime + 10
                            )
                            ref.current.currentTime = newTime
                            setCurrentTime(Math.floor(newTime))
                        }
                    }}
                />
            </Buttons>
            <AudioProgressSlider
                bufferedTime={bufferedTime}
                currentTime={currentTime}
                maxTime={maxTime}
                onChange={(value) => {
                    if (ref.current) {
                        ref.current.pause()
                        setCurrentTime(Math.min(Math.floor(value), maxTime))
                        ref.current.currentTime = Math.min(
                            Math.floor(value),
                            maxTime
                        )

                        if (isPlaying) {
                            // Due Safari issue, pause then replay audio
                            ref.current.play()
                        }
                    }
                }}
            />
        </Container>
    )
}
